import React, { useState, useEffect } from 'react';
import config from '../../config';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  useTheme,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PrintIcon from '@mui/icons-material/Print';
import EditIcon from '@mui/icons-material/Edit';
import LocalPizzaIcon from '@mui/icons-material/LocalPizza';
import { useLanguage } from '../../contexts/LanguageContext';
import { BranchIssueDto } from '../../types/branchIssues.types';
import { companyInfoService, CompanyInfo } from '../../services/companyInfo.service';

const ORANGE = '#e65100';

interface BranchIssuePreviewDialogProps {
  open: boolean;
  onClose: () => void;
  issue: BranchIssueDto | null;
  onEdit?: () => void;
}

const BranchIssuePreviewDialog: React.FC<BranchIssuePreviewDialogProps> = ({
  open,
  onClose,
  issue,
  onEdit
}) => {
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);

  useEffect(() => {
    if (open) {
      companyInfoService.getCompanyInfo().then(setCompanyInfo).catch(() => {});
    }
  }, [open]);

  if (!issue) return null;

  const getStatusLabel = (status?: string) => {
    switch (status) {
      case 'Draft': return isRTL ? 'مسودة' : 'Draft';
      case 'Posted': return isRTL ? 'مرحّل' : 'Posted';
      default: return status || '-';
    }
  };

  const getStatusColor = (status?: string): 'default' | 'primary' | 'success' | 'warning' | 'error' => {
    switch (status) {
      case 'Draft': return 'default';
      case 'Posted': return 'success';
      default: return 'default';
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString(isRTL ? 'ar-KW' : 'en-GB');
  };

  const formatNumber = (num?: number) => (num ?? 0).toFixed(3);

  const canEdit = issue.status === 'Draft';

  const handlePrint = () => {

    const companyHeader = companyInfo ? `
      <div style="text-align:center;border-bottom:2px solid ${ORANGE};padding-bottom:10px;margin-bottom:15px;">
        ${companyInfo.logoPath ? `<img src="${config.API_ORIGIN}${companyInfo.logoPath}" style="max-height:50px;margin-bottom:5px;" onerror="this.style.display='none'" />` : ''}
        <h2 style="color:${ORANGE};margin:5px 0;">${isRTL ? companyInfo.companyNameAr : companyInfo.companyName}</h2>
        <p style="font-size:11px;margin:0;color:#666;">
          ${companyInfo.address ? companyInfo.address + ' | ' : ''}${companyInfo.phone ? companyInfo.phone + ' | ' : ''}${companyInfo.email || ''}${companyInfo.website ? ' | ' + companyInfo.website : ''}
        </p>
      </div>
    ` : '';

    const docTitle = isRTL ? 'إصدار للفرع' : 'Branch Issue';

    const detailRows = issue.details && issue.details.length > 0
      ? issue.details.map((d, i) => `
        <tr>
          <td style="text-align:center;">${i + 1}</td>
          <td><strong>${d.itemCode}</strong><br/><span style="color:#666;font-size:11px;">${isRTL ? d.itemNameAr : d.itemName}</span></td>
          <td>${isRTL ? d.unitNameAr : d.unitName}</td>
          <td style="text-align:center;font-weight:bold;">${formatNumber(d.quantity)}</td>
          <td style="text-align:center;">${formatNumber(d.unitCost || d.unitPrice || 0)}</td>
          <td style="text-align:center;font-weight:bold;">${formatNumber(d.totalPrice || d.quantity * (d.unitCost || d.unitPrice || 0))}</td>
          <td>${d.notes || '-'}</td>
        </tr>
      `).join('')
      : `<tr><td colspan="7" style="text-align:center;color:#999;">${isRTL ? 'لا توجد تفاصيل' : 'No details'}</td></tr>`;

    const totalQty = issue.details?.reduce((s, d) => s + d.quantity, 0) || 0;
    const totalCost = issue.details?.reduce((s, d) => s + (d.totalPrice || d.quantity * (d.unitCost || d.unitPrice || 0)), 0) || 0;

    const html = `
      <html dir="${isRTL ? 'rtl' : 'ltr'}">
      <head>
        <title>${docTitle} - ${issue.issueNumber}</title>
        <style>
          * { box-sizing: border-box; margin: 0; padding: 0; }
          body { font-family: 'Segoe UI', Tahoma, Arial, sans-serif; padding: 20px; font-size: 13px; color: #333; direction: ${isRTL ? 'rtl' : 'ltr'}; }
          table { width: 100%; border-collapse: collapse; margin-top: 10px; }
          th, td { border: 1px solid #ddd; padding: 8px 10px; font-size: 12px; }
          th { background: ${ORANGE}; color: white; font-weight: bold; }
          tr:nth-child(even) { background: #f9f9f9; }
          .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 15px; }
          .info-item label { font-size: 11px; color: #999; display: block; }
          .info-item span { font-weight: bold; font-size: 14px; }
          .branch-box { padding: 12px; border-radius: 6px; text-align: center; background: #fff3e0; border: 1px solid #ffcc80; }
          .status { display: inline-block; padding: 3px 12px; border-radius: 12px; font-size: 11px; font-weight: bold; color: white; }
          .status-Draft { background: #9e9e9e; }
          .status-Posted { background: #2e7d32; }
          .signatures { display: flex; justify-content: space-between; margin-top: 60px; }
          .sig-box { text-align: center; width: 30%; }
          .sig-line { border-top: 1px solid #000; padding-top: 5px; margin-top: 60px; }
          @page { size: A4; margin: 1cm; }
        </style>
      </head>
      <body>
        ${companyHeader}
        <h1 style="text-align:center;color:${ORANGE};margin:10px 0 20px;">${docTitle}</h1>

        <div style="border:2px solid ${ORANGE};border-radius:8px;padding:15px;margin-bottom:20px;">
          <div class="info-grid">
            <div class="info-item">
              <label>${isRTL ? 'رقم الإصدار' : 'Issue Number'}</label>
              <span style="color:${ORANGE};">${issue.issueNumber}</span>
            </div>
            <div class="info-item">
              <label>${isRTL ? 'التاريخ' : 'Date'}</label>
              <span>${formatDate(issue.issueDate)}</span>
            </div>
            <div class="info-item">
              <label>${isRTL ? 'الحالة' : 'Status'}</label>
              <span class="status status-${issue.status}">${getStatusLabel(issue.status)}</span>
            </div>
            ${issue.journalEntryId ? `
            <div class="info-item">
              <label>${isRTL ? 'رقم القيد' : 'Journal Entry'}</label>
              <span style="color:#2e7d32;">${issue.journalEntryNumber || ''} (#${issue.journalEntryId})</span>
            </div>` : ''}
          </div>

          <hr style="border:none;border-top:1px solid #ddd;margin:10px 0;" />

          <div style="display:flex;justify-content:center;margin:15px 0;">
            <div class="branch-box" style="min-width:250px;">
              <div style="font-size:11px;color:#999;">${isRTL ? 'الفرع المستلم' : 'Receiving Branch'}</div>
              <div style="font-weight:bold;color:${ORANGE};font-size:15px;">${isRTL ? (issue.branchName || '') : (issue.branchName || '')}</div>
              <div style="font-size:11px;color:#999;">${issue.branchCode || ''}</div>
            </div>
          </div>

          ${issue.notes ? `<div style="margin-top:10px;"><label style="font-size:11px;color:#999;">${isRTL ? 'ملاحظات:' : 'Notes:'}</label><p>${issue.notes}</p></div>` : ''}
        </div>

        <table>
          <thead>
            <tr>
              <th>#</th>
              <th>${isRTL ? 'الصنف' : 'Item'}</th>
              <th>${isRTL ? 'الوحدة' : 'Unit'}</th>
              <th>${isRTL ? 'الكمية' : 'Quantity'}</th>
              <th>${isRTL ? 'تكلفة الوحدة' : 'Unit Cost'}</th>
              <th>${isRTL ? 'الإجمالي' : 'Total'}</th>
              <th>${isRTL ? 'ملاحظات' : 'Notes'}</th>
            </tr>
          </thead>
          <tbody>
            ${detailRows}
            <tr style="background:#fff3e0;font-weight:bold;">
              <td colspan="3" style="text-align:${isRTL ? 'left' : 'right'};">${isRTL ? 'الإجمالي:' : 'Total:'}</td>
              <td style="text-align:center;">${formatNumber(totalQty)}</td>
              <td></td>
              <td style="text-align:center;">${formatNumber(totalCost)}</td>
              <td></td>
            </tr>
          </tbody>
        </table>

        <div class="signatures">
          <div class="sig-box"><div class="sig-line">${isRTL ? 'مدير الفرع' : 'Branch Manager'}</div></div>
          <div class="sig-box"><div class="sig-line">${isRTL ? 'أمين المخزن' : 'Warehouse Keeper'}</div></div>
          <div class="sig-box"><div class="sig-line">${isRTL ? 'المحاسب' : 'Accountant'}</div></div>
        </div>
      </body>
      </html>
    `;

    const w = window.open('', '_blank', 'width=900,height=700');
    if (w) {
      w.document.write(html);
      w.document.close();
      w.focus();
      setTimeout(() => { w.print(); }, 500);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      dir={isRTL ? 'rtl' : 'ltr'}
      PaperProps={{ sx: { borderRadius: 3, bgcolor: isDarkMode ? '#1a1a2e' : 'white' } }}
    >
      <DialogTitle
        sx={{
          background: isDarkMode
            ? `linear-gradient(135deg, #bf360c 0%, ${ORANGE} 100%)`
            : `linear-gradient(135deg, ${ORANGE} 0%, #ff8f00 100%)`,
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          py: 2
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LocalPizzaIcon />
          <Box component="span">{isRTL ? 'معاينة إصدار الفرع' : 'Branch Issue Preview'}</Box>
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'white' }}><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent sx={{ mt: 3 }}>
        {/* Header Info */}
        <Paper elevation={2} sx={{ p: 3, mb: 3, bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#f5f5f5', borderRadius: 2 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                {isRTL ? 'رقم الإصدار' : 'Issue Number'}
              </Typography>
              <Typography variant="h6" fontWeight="bold" sx={{ color: ORANGE }}>{issue.issueNumber}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                {isRTL ? 'التاريخ' : 'Date'}
              </Typography>
              <Typography variant="body1" fontWeight="bold">{formatDate(issue.issueDate)}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                {isRTL ? 'الحالة' : 'Status'}
              </Typography>
              <Box><Chip label={getStatusLabel(issue.status)} color={getStatusColor(issue.status)} size="small" sx={{ fontWeight: 'bold' }} /></Box>
            </Box>
            {issue.journalEntryId && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                  {isRTL ? 'رقم القيد' : 'Journal Entry'}
                </Typography>
                <Typography variant="body1" fontWeight="bold" color="success.main">{issue.journalEntryNumber || ''} (#{issue.journalEntryId})</Typography>
              </Box>
            )}
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Branch */}
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Box sx={{ p: 2, bgcolor: isDarkMode ? 'rgba(230,81,0,0.1)' : '#fff3e0', borderRadius: 1, minWidth: 250, textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                {isRTL ? 'الفرع المستلم' : 'Receiving Branch'}
              </Typography>
              <Typography variant="body1" fontWeight="bold" sx={{ color: ORANGE }}>
                {issue.branchName || ''}
              </Typography>
              <Typography variant="caption" color="text.secondary">{issue.branchCode}</Typography>
            </Box>
          </Box>

          {issue.notes && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ fontSize: '0.75rem' }}>
                {isRTL ? 'ملاحظات:' : 'Notes:'}
              </Typography>
              <Typography variant="body2">{issue.notes}</Typography>
            </Box>
          )}
        </Paper>

        {/* Items Details */}
        <Paper elevation={2} sx={{ mb: 2, borderRadius: 2, overflow: 'hidden' }}>
          <Box sx={{ bgcolor: isDarkMode ? 'rgba(230,81,0,0.3)' : ORANGE, color: 'white', p: 1.5, fontWeight: 'bold' }}>
            {isRTL ? '📦 تفاصيل الأصناف' : '📦 Items Details'}
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>{isRTL ? 'الصنف' : 'Item'}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>{isRTL ? 'الوحدة' : 'Unit'}</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>{isRTL ? 'الكمية' : 'Quantity'}</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>{isRTL ? 'تكلفة الوحدة' : 'Unit Cost'}</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>{isRTL ? 'الإجمالي' : 'Total'}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>{isRTL ? 'ملاحظات' : 'Notes'}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {issue.details && issue.details.length > 0 ? (
                  issue.details.map((d, idx) => (
                    <TableRow key={d.branchIssueDetailId || idx} hover>
                      <TableCell sx={{ fontSize: '0.8rem' }}>{idx + 1}</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem' }}>
                        <strong>{d.itemCode}</strong><br />
                        <Typography variant="caption" color="text.secondary">{isRTL ? d.itemNameAr : d.itemName}</Typography>
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.8rem' }}>{isRTL ? d.unitNameAr : d.unitName}</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.85rem', fontWeight: 'bold' }}>{formatNumber(d.quantity)}</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.85rem' }}>{formatNumber(d.unitCost || d.unitPrice || 0)}</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.85rem', fontWeight: 'bold', color: ORANGE }}>{formatNumber(d.totalPrice || d.quantity * (d.unitCost || d.unitPrice || 0))}</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem' }}>{d.notes || '-'}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography variant="body2" color="text.secondary">{isRTL ? 'لا توجد تفاصيل' : 'No details'}</Typography>
                    </TableCell>
                  </TableRow>
                )}
                {issue.details && issue.details.length > 0 && (
                  <TableRow>
                    <TableCell colSpan={3} align={isRTL ? 'left' : 'right'} sx={{ fontWeight: 'bold', bgcolor: `rgba(230,81,0,0.1)`, fontSize: '0.85rem' }}>
                      {isRTL ? 'الإجمالي:' : 'Total:'}
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: `rgba(230,81,0,0.1)` }}>
                      <Chip label={formatNumber(issue.details.reduce((s, d) => s + d.quantity, 0))} sx={{ bgcolor: ORANGE, color: 'white', fontWeight: 'bold' }} size="small" />
                    </TableCell>
                    <TableCell sx={{ bgcolor: `rgba(230,81,0,0.1)` }} />
                    <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: `rgba(230,81,0,0.1)`, color: ORANGE }}>
                      {formatNumber(issue.details.reduce((s, d) => s + (d.totalPrice || d.quantity * (d.unitCost || d.unitPrice || 0)), 0))}
                    </TableCell>
                    <TableCell sx={{ bgcolor: `rgba(230,81,0,0.1)` }} />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </DialogContent>

      <DialogActions sx={{ p: 3, bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#f5f5f5' }}>
        <Button onClick={onClose} size="large">{isRTL ? 'إغلاق' : 'Close'}</Button>
        {canEdit && onEdit && (
          <Button variant="outlined" startIcon={<EditIcon />} onClick={onEdit} size="large" sx={{ color: ORANGE, borderColor: ORANGE }}>
            {isRTL ? 'تعديل' : 'Edit'}
          </Button>
        )}
        <Button
          variant="contained"
          startIcon={<PrintIcon />}
          onClick={handlePrint}
          size="large"
          sx={{ background: isDarkMode ? `linear-gradient(45deg, #bf360c 30%, ${ORANGE} 90%)` : `linear-gradient(45deg, ${ORANGE} 30%, #ff8f00 90%)` }}
        >
          {isRTL ? 'طباعة' : 'Print'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BranchIssuePreviewDialog;
